<template>
<div class="flights-list-page">

  <div class="flights-search">
    <div class="container">
      <div class="row">
        <FlightSearch :showSearchWidget="false" :showSearchToggle="true" />
      </div>
    </div>
  </div>

  <div class="loading-widget-search" v-if="loading">
    <div class="loader"></div>
    <strong>{{ $t('pleaseWaitAMoment') }}</strong> 
    <span>... {{ $t('whileWeFindTheCheapestFlightPricesForYou') }}</span>
  </div>

  <div class="container">
    <div class="row">
        <div class="page-body">
          <div class="search-list-body">

            <aside class="filter-col">
              <div v-if="!loading && !inlineLoading && !noResults">
                
                <button class="btn filter-toggle-btn" title="toggle Filters" @click="toggleFilters()">
                  <i class="fal fa-filter"></i> 
                  <span v-if="showFilters">{{ $t('hideFlightFilters') }}</span>
                  <span v-else>{{ $t('showFlightFilters') }}</span>
                </button>
                
                <div class="filter-groups" :class="{ 'show-filters': showFilters }">
                
                  <div class="filter-group" v-if="apiFlights && apiFlights.filters && apiFlights.filters.price">
                    <h4 class="flex">
                      {{$t('maximumPrice')}}
                      <span class="value">{{filters.priceFilter | formatCurrency(apiFlights.offers.currency, 0)}}</span>
                    </h4>
                    <div class="price-filter">
                      <range-slider
                        class="slider"
                        :min="apiFlights.filters.price.min"
                        :max="apiFlights.filters.price.max"
                        step="1"
                        v-model="filters.priceFilter">
                      </range-slider>
                    </div>
                  </div>

                  <div class="filter-group">
                    <h4>{{$t('stops')}}</h4>
                    <div class="checkbox-filters">
                      <ul>
                        <li>
                          <label for="allStopsChecked">
                            <input type="checkbox" id="allStopsChecked" v-model="allStopsChecked" />
                            <span class="checkmark"></span>
                            {{$t('selectAll')}}
                          </label>
                        </li>
                        <li v-for="(stop, stopIndex) in filters.stops" :key="stopIndex" @mouseover="stop.showOnly = true" @mouseleave="stop.showOnly = false">
                          <label :for="stopIndex">
                            <input type="checkbox" :id="stopIndex" v-model="stop.checked" />
                            <span class="checkmark"></span>
                            {{stop.label}}
                            <span class="count">({{stop.count}})</span>
                            <a v-if="stop.showOnly" @click="onlySelectStop(stop)" class="single-filter">only</a>
                          </label>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div class="filter-group">
                    <h4>{{$t('seatClasses')}}</h4>
                    <div class="checkbox-filters">
                      <ul>
                        <li>
                          <label for="allSeatClassesChecked">
                            <input type="checkbox" id="allSeatClassesChecked" v-model="allSeatClassesChecked" />
                            <span class="checkmark"></span>
                            {{$t('selectAll')}}
                          </label>
                        </li>
                        <li v-for="(seatClass, seatClassIndex) in seatClasses" :key="seatClassIndex" @mouseover="seatClass.showOnly = true" @mouseleave="seatClass.showOnly = false">
                          <label :for="seatClassIndex">
                            <input type="checkbox" :id="seatClassIndex" v-model="seatClass.checked" />
                            <span class="checkmark"></span>
                            {{$t(seatClass.code)}}
                            <span class="count">({{seatClass.count}})</span>
                            <a v-if="seatClass.showOnly" @click="onlySelectSeatClass(seatClass)" class="single-filter">only</a>
                          </label>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div class="filter-group">
                    <h4>{{$t('airlines')}}</h4>
                    <div class="checkbox-filters" v-if="apiFlights && apiFlights.filters">
                      <ul>
                        <li>
                          <label for="allAirlinesChecked">
                            <input type="checkbox" id="allAirlinesChecked" v-model="allAirlinesChecked" />
                            <span class="checkmark"></span>
                            {{$t('selectAll')}}
                          </label>
                        </li>
                        <li v-for="(airline, index) in airlines" :key="index" @mouseover="airline.showOnly = true" @mouseleave="airline.showOnly = false">
                          <label :for="'filterAirline' + airline.code">
                            <input type="checkbox" v-model="airline.checked" :id="'filterAirline' + airline.code" />
                            <span class="checkmark"></span>
                            <span class="logo"><img :src="'https://assets.totaltravel.io/img/airlines/' + airline.code + '.png'" :alt="airline.code | toAirlineName" /></span>
                            <span class="name">{{airline.code | toAirlineName}}</span>
                            <a v-if="airline.showOnly" @click="onlySelectAirline(airline)" class="single-filter">{{ $t('only') }}</a>
                          </label>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <a class="clear-link" v-if="filtersModified" @click="resetFilters()">
                    <i class="fal fa-times-circle"></i>
                    <span class="text">{{$t('clearFilters')}}</span>
                  </a>

                </div>
              </div>

              <LoadingSearchFilters v-else />
            </aside>


            <section class="search-list-col">
            
              <div class="listing-filters" v-if="!loading && apiFlights.offers && offersList.length > 0">
                <ul>
                  <li :class="$route.query.sortOrder.toLowerCase() === 'cheapest' ? 'active' : ''">
                    <router-link :to="getUrlWithoutSortOrder() + '&sortOrder=cheapest'">
                      <i class="fal fa-money-bill-wave"></i> 
                      {{$t('cheapest')}}
                    </router-link>
                  </li>
                  <li :class="$route.query.sortOrder.toLowerCase() === 'best' ? 'active' : ''">
                    <router-link :to="getUrlWithoutSortOrder() + '&sortOrder=best'">
                      <i class="fal fa-trophy-alt"></i> 
                      {{$t('best')}}
                    </router-link>
                  </li>
                  <li :class="$route.query.sortOrder.toLowerCase() === 'quickest' ? 'active' : ''">
                    <router-link :to="getUrlWithoutSortOrder() + '&sortOrder=quickest'">
                      <i class="fal fa-fast-forward"></i> 
                      {{$t('quickest')}}
                    </router-link>
                  </li>
                </ul>
              </div>
              
              <div v-if="!noResults && apiFlights && apiFlights.offers && !loading && !inlineLoading">
                <FlightListing
                  v-for="(listing, index) in offersList.slice(0, itemsToShow)"
                  :currency="apiFlights.offers.currency"
                  :key="index"
                  :listing="listing"
                  :searchOrigin="searchOrigin"
                  :searchDestination="searchDestination"
                  :departureToReturnDays="departureToReturnDays"/>
                
                <button class="load-more" v-if="offersList.length > itemsToShow" @click="itemsToShow += 10">{{$t('loadMore')}}</button>
              </div>
              
              <span v-if="loading || inlineLoading">
                <LoadingFlightListing v-for="(n, index) in 5" :key="index" />
              </span>

              <div v-if="noResults || apiFlights.offers && offersList.length < 1" class="no-results">
                <h3>{{$t('removeFiltersParagraph')}}</h3>
                <div v-if="filtersModified">
                  <p></p>
                  <p><a @click="resetFilters()" title="Clear Search Filters">{{$t('clearFilters')}}</a></p>
                </div>
                <div v-else>
                  <p>{{$t('noSearchResultsParagraph')}}</p>
                </div>
              </div>
            </section>

          </div>
        </div>
    </div>
  </div>
</div>
</template>

<script>

import FlightSearchWidget from '@/components/flights/FlightSearchWidget'
import FlightListing from '@/components/flights/FlightListing'
import LoadingFlightListing from '@/components/flights/LoadingFlightListing'
import LoadingSearchFilters from '@/components/LoadingSearchFilters'
import RangeSlider from 'vue-range-slider'
import FlightsApi from '../../services/flightsApi'
import 'vue-range-slider/dist/vue-range-slider.css'
import {mapGetters} from "vuex";
import { analytics } from "../../firebaseConfig";


export default {
  name: 'FlightsList',
  data () {
    let self = this;
    return {
      api: {},
      noResults: false,
      airlineApi: {},
      loading: false,
      inlineLoading: false,
      title: 'Flights from..',
      travelTimeFilter: 0,
      apiFlights: {},
      itemsToShow: 20,
      showFilters: false,
      airlines: [],
      seatClasses: [],
      departureToReturnDays: 0,
      searchOrigin: this.$route.params.origin,
      searchDestination: this.$route.params.destination,
      filters: {
        selectedAirlines: [],
        priceFilter: 0, 
        stops: {
          direct: {count: 0, label: self.$t('direct'), checked: true, showOnly: false},
          oneStop: {count: 0, label: self.$t('oneStop'), checked: true, showOnly: false},
          twoPlusStops: {count: 0, label: self.$t('twoPlusStops'), checked: true, showOnly: false}
        },
        selectedSeatClasses: []
      }
    }
  },

  metaInfo() {
    let self = this;
    return {
      title: self.$t('flightSearchResults'),
    }
  },

  components: {
    'FlightSearch': FlightSearchWidget,
    FlightListing,
    LoadingFlightListing,
    LoadingSearchFilters,
    RangeSlider,
  },
  watch: {
    '$route': function() {
      this.fetchData(false);
    },

    'airlines': {
      handler: function (newFilters) {
        let result = [];
          for(let i in newFilters) {
            if (newFilters[i].checked) {
              result.push(newFilters[i].code);
            }
          }
        if (JSON.stringify(this.filters.selectedAirlines) !== JSON.stringify(result)) {
          this.filters.selectedAirlines = result;
        }
      },
      deep: true
    },

    'seatClasses': {
      handler: function (newFilters) {
        let result = [];
        for(let i in newFilters) {
          if (newFilters[i].checked) {
            result.push(newFilters[i].code);
          }
        }
        if (JSON.stringify(this.filters.selectedSeatClasses) !== JSON.stringify(result)) {
          this.filters.selectedSeatClasses = result;
        }
      },
      deep: true
    }
  },
  methods: {
    resetFilters() {
      this.allAirlinesChecked = true;
      this.allStopsChecked = true;
      this.allSeatClassesChecked = true;
      this.filters.priceFilter = this.apiFlights.filters.price.max;
    },
    onlySelectStop(stop) {
      this.allStopsChecked = false;
      setTimeout(function() {stop.checked = true;}, 500);
    },
    onlySelectSeatClass(seatClass) {
      this.allSeatClassesChecked = false;
      setTimeout(function() {seatClass.checked = true;}, 500);
    },
    onlySelectAirline(airline) {
      this.allAirlinesChecked = false;
      setTimeout(function() { airline.checked = true;}, 500);
    },
    fetchData(isFresh) {
      let self = this;
      let params = this.$route.params;
      let query = this.$route.query;

      let key = `${params.currency}|${params.origin}|${params.destination}|${params.departureDate}|${params.returnDate}|${query.adults}|${query.children}|${query.infants}|${query.seatClass}|${query.sortOrder}`;

      self.departureToReturnDays = params.returnDate ? self.$moment(params.returnDate).diff(self.$moment(params.departureDate), 'days') + 1: 0;

      let existingResults = self.getSearchResults(key);

      self.noResults = false;

      if (existingResults && (existingResults.timestamp.getTime() + 600000) > new Date().getTime()) {
        self.setupData(existingResults);
      }
      else {
        if (isFresh) {
          self.loading = true;
        } else {
          self.inlineLoading = true;
        }

        self.api.search(
          params.origin,
          params.destination,
          params.departureDate,
          params.returnDate,
          query.adults,
          query.children,
          query.infants,
          query.seatClass,
          query.sortOrder,
          params.currency,
          200)
          .then((result) => {
            self.setupData(result.data);
            self.$store.dispatch('saveSearchResult', {key: key, result: result.data});
          })
          .catch((err) => {
            self.loading = false;
            self.inlineLoading = false;
            self.noResults = true;
            self.error = err;
          });
      }

      analytics.logEvent('search', {
        agency: window.TravelPlatform.key,
        type: 'flight',
        origin: params.origin,
        destination: params.destination,
        departureDate: params.departureDate,
        returnDate: params.returnDate,
        adults: query.adults,
        children: query.children,
        infants: query.infants,
        currency: params.currency
      })

    },
    setupData(result) {
      let self = this;
      self.apiFlights = result;
      self.apiFlights.timestamp = new Date();
      self.loading = false;
      self.itemsToShow = 10;
      for(let i in self.apiFlights.offers.offerList) {
        let listing = self.apiFlights.offers.offerList[i];
        listing.showAvailDetails = false;
      }

      if (self.apiFlights && self.apiFlights.filters) {
        let filters = self.apiFlights.filters;
        filters.price.max = Math.ceil(filters.price.max);
        self.filters.priceFilter = filters.price.max;

        self.filters.stops.direct.count = filters.stops.direct;
        self.filters.stops.oneStop.count = filters.stops.oneStop;
        self.filters.stops.twoPlusStops.count = filters.stops.twoPlusStops;

        self.seatClasses = [];

        for(let seatClassLabel in filters.seatClasses) {
          let item = {
            count: filters.seatClasses[seatClassLabel],
            showOnly: false,
            code: seatClassLabel,
            checked: true};
          self.seatClasses.push(item);
        }

        self.airlines = [];
        if (filters.airlines && filters.airlines.length > 0) {
          for(let i in filters.airlines) {
            self.airlines.push({code: filters.airlines[i], checked: true, showOnly: false});
          }
        }
      }
      self.inlineLoading = false;
    },
    getUrlWithoutSortOrder() {
      let fullPath = this.$route.fullPath;
      let pathBeforeSortOrder = fullPath.split("&sortOrder")[0];
      return pathBeforeSortOrder;
    },

    toggleFilters() {
      this.showFilters = !this.showFilters;
    }

  },
  mounted () {
    this.api = new FlightsApi();
    this.fetchData(true);
  },
  computed: {
    ...mapGetters ([
      'getSearchResults',
      'getCurrencyCode'
    ]),
    filtersModified: function () {
      if (this.apiFlights && this.apiFlights.filters) {
        return !(this.allAirlinesChecked && this.filters.priceFilter === this.apiFlights.filters.price.max && this.allStopsChecked && this.allSeatClassesChecked);
      }
      return false;
    },
    offersList: function () {
      if (this.apiFlights) {
        let priceFilter = this.filters.priceFilter;
        let stops = this.filters.stops;
        let selectedSeatClasses = this.filters.selectedSeatClasses;
        let selectedAirlines = this.filters.selectedAirlines;
        return this.apiFlights.offers.offerList.filter((offer) => {
          return (offer.price.total < priceFilter)
              && (offer.direct == stops.direct.checked || offer.oneStop == stops.oneStop.checked || offer.twoPlusStops == stops.twoPlusStops.checked)
              && (offer.airlines.length <= selectedAirlines.length)
              && (offer.airlines.every((airline) => selectedAirlines.includes(airline)))
              && (selectedSeatClasses.includes(offer.seatClass))
        });
      }
      return [];
    },
    allStopsChecked: {
      get: function () {
        let result = true;
        if (this.filters.stops) {
          let keys = Object.keys(this.filters.stops);
          for (let i in keys) {
            result = result && this.filters.stops[keys[i]].checked;
          }
        }
        return result;
      },
      set: function (value) {
        if (this.filters.stops) {
          let keys = Object.keys(this.filters.stops);
          for(let i in keys) {
            this.filters.stops[keys[i]].checked = value;
          }
        }
      }
    },
    allSeatClassesChecked: {
      get: function () {
        let result = true;
        if (this.seatClasses) {
          let keys = Object.keys(this.seatClasses);
          for (let i in keys) {
            result = result && this.seatClasses[keys[i]].checked;
          }
        }
        return result;
      },
      set: function (value) {
        if (this.seatClasses) {
          let keys = Object.keys(this.seatClasses);
          for(let i in keys) {
            this.seatClasses[keys[i]].checked = value;
          }
        }
      }
    },
    allAirlinesChecked: {
      get: function () {
        let result = true;
        if (this.airlines && this.airlines.length > 0) {
          for(let i in this.airlines) {
            result = result && this.airlines[i].checked;
          }
        }
        return result;
      },
      set: function (value) {
        if (this.airlines && this.airlines.length > 0) {
          for(let i in this.airlines) {
            this.airlines[i].checked = value;
          }
        }
      }
    }
  },
}
</script>
